/**
 * @author Harish.R <harish.r@314ecorp.com>
 * @description Print Eligibility Response Button
 */
import { useMemo, useState, useRef } from 'react';
import _ from 'lodash';
import { Col, Divider, Row, Button as AntButton, ButtonProps } from 'antd';
import { useReactToPrint } from 'react-to-print';

import EventTracker from 'utils/eventTracker';
import { EVENTS } from 'app-constants/events';

/**
 * interface for props
 */
export interface IProps {
	id: string;
	patientName: string;
	exportItems?: EligibilityResponseSchema.IExportSchema[];
	label?: React.ReactNode;
}

const exportNodeStyles: React.CSSProperties = {
	position: 'fixed',
	left: 0,
	top: 0,
	width: 0,
	height: 0,
	visibility: 'hidden',
};

/**
 * PrintEligibilityResponse
 *
 */
const PrintEligibilityResponse: React.FC<IProps & ButtonProps> = ({ patientName, exportItems, label, ...restProps }) => {
	const [isPrinting, setIsPrinting] = useState(false);
	const exportRef = useRef(null) as React.RefObject<HTMLDivElement>;

	const printResponse = useReactToPrint({
		documentTitle: `Eligibility-Response-${_.kebabCase(patientName)}`,
		contentRef: exportRef,
		bodyClass: 'eligibility-export-node-content',
	});

	const handlePrint = () => {
		if (!isPrinting) {
			setIsPrinting(true);
		}
		EventTracker.trackEvent(EVENTS.eligibilityResponse.printResponse());
		setTimeout(printResponse, 100);
	};

	const exportContent = useMemo(() => {
		const orderedItems = _.orderBy(exportItems, (item) => item.pdfExport.order);
		return (
			<Row ref={exportRef} gutter={[0, 40]}>
				{_.map(orderedItems, (item, key) => (
					<Col span={24} key={key}>
						<h2>{item.label}</h2>
						<Divider />
						{item.pdfExport.content}
					</Col>
				))}
			</Row>
		);
	}, [exportItems]);

	return (
		<>
			<AntButton {...restProps} onClick={handlePrint}>
				{label || 'Print/Download Response'}
			</AntButton>
			{isPrinting && <div style={exportNodeStyles}>{exportContent}</div>}
		</>
	);
};

export { exportNodeStyles };
export default PrintEligibilityResponse;
